import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
const sections = [{
  id: 'introduction',
  title: '1. Introduction'
}, {
  id: 'information-we-collect',
  title: '2. Information We Collect'
}, {
  id: 'how-we-use',
  title: '3. How We Use Your Information'
}, {
  id: 'sharing',
  title: '4. Sharing of Information'
}, {
  id: 'cookies',
  title: '5. Cookies and Tracking'
}, {
  id: 'security',
  title: '6. Data Storage and Security'
}, {
  id: 'international',
  title: '7. International Data Transfers'
}, {
  id: 'rights',
  title: '8. Your Rights'
}, {
  id: 'third-party',
  title: '9. Third-Party Links'
}, {
  id: 'updates',
  title: '10. Updates to This Policy'
}, {
  id: 'contact',
  title: '11. Contact Us'
}];
export function PrivacyPolicy() {
  const [activeSection, setActiveSection] = useState(sections[0].id);
  useEffect(() => {
    window.scrollTo(0, 0);
    const handleScroll = () => {
      const sectionElements = sections.map(s => document.getElementById(s.id));
      const scrollPosition = window.scrollY + 200; // Offset for sticky header
      for (let i = sectionElements.length - 1; i >= 0; i--) {
        const section = sectionElements[i];
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(sections[i].id);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 120; // Header offset
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };
  return <main className="relative z-10 pt-32 pb-24 min-h-screen" data-id="element-1070">
      <div className="max-w-7xl mx-auto px-6" data-id="element-1071">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="max-w-3xl mb-16" data-id="element-1072">
          <h1 className="text-4xl md:text-5xl font-display mb-6" data-id="element-1073">
            Privacy Policy
          </h1>
          <p className="text-xl text-gray-400 font-medium mb-4" data-id="element-1074">
            How Aijentik collects, uses, and protects your information
          </p>
          <p className="text-sm text-brand-orange" data-id="element-1075">Last Updated: April 2026</p>
        </motion.div>

        <div className="flex flex-col lg:flex-row gap-16 relative" data-id="element-1076">
          {/* Main Content */}
          <div className="flex-1 max-w-3xl" data-id="element-1077">
            <div className="prose prose-invert prose-gray max-w-none" data-id="element-1078">
              {/* Section 1 */}
              <motion.section id="introduction" className="mb-16 scroll-mt-32" initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true,
              margin: '-100px'
            }} data-id="element-1079">
                <h2 className="text-2xl font-display mb-6 text-white" data-id="element-1080">
                  1. Introduction
                </h2>
                <div className="text-gray-300 space-y-4 leading-relaxed" data-id="element-1081">
                  <p data-id="element-1082">
                    Aijentik ("we", "our", "us") respects your privacy and is
                    committed to protecting your personal information. This
                    Privacy Policy explains how we collect, use, disclose, and
                    safeguard your information when you visit our website
                    aijentik.com or engage with our services.
                  </p>
                  <p data-id="element-1083">
                    We comply with applicable privacy laws, including the
                    Privacy Act 1988 (Cth) and international data protection
                    standards where applicable.
                  </p>
                </div>
              </motion.section>

              <div className="h-px w-full bg-white/5 my-12" data-id="element-1084" />

              {/* Section 2 */}
              <motion.section id="information-we-collect" className="mb-16 scroll-mt-32" initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true,
              margin: '-100px'
            }} data-id="element-1085">
                <h2 className="text-2xl font-display mb-6 text-white" data-id="element-1086">
                  2. Information We Collect
                </h2>
                <div className="text-gray-300 space-y-6 leading-relaxed" data-id="element-1087">
                  <p data-id="element-1088">We may collect the following types of information:</p>

                  <div data-id="element-1089">
                    <h3 className="text-lg font-semibold text-white mb-3" data-id="element-1090">
                      a. Personal Information
                    </h3>
                    <ul className="list-disc pl-5 space-y-2 marker:text-brand-orange" data-id="element-1091">
                      <li data-id="element-1092">Name</li>
                      <li data-id="element-1093">Email address</li>
                      <li data-id="element-1094">Phone number</li>
                      <li data-id="element-1095">Company name</li>
                      <li data-id="element-1096">
                        Any information submitted via forms (e.g. demo requests,
                        AI reports)
                      </li>
                    </ul>
                  </div>

                  <div data-id="element-1097">
                    <h3 className="text-lg font-semibold text-white mb-3" data-id="element-1098">
                      b. Usage Data
                    </h3>
                    <ul className="list-disc pl-5 space-y-2 marker:text-brand-orange" data-id="element-1099">
                      <li data-id="element-1100">IP address</li>
                      <li data-id="element-1101">Browser type</li>
                      <li data-id="element-1102">Device information</li>
                      <li data-id="element-1103">Pages visited and time spent</li>
                      <li data-id="element-1104">Referral source</li>
                    </ul>
                  </div>

                  <div data-id="element-1105">
                    <h3 className="text-lg font-semibold text-white mb-3" data-id="element-1106">
                      c. AI Interaction Data
                    </h3>
                    <ul className="list-disc pl-5 space-y-2 marker:text-brand-orange" data-id="element-1107">
                      <li data-id="element-1108">Inputs provided into AI tools or forms</li>
                      <li data-id="element-1109">Website URLs submitted for analysis</li>
                      <li data-id="element-1110">
                        Generated outputs (for service delivery and improvement)
                      </li>
                    </ul>
                  </div>
                </div>
              </motion.section>

              <div className="h-px w-full bg-white/5 my-12" data-id="element-1111" />

              {/* Section 3 */}
              <motion.section id="how-we-use" className="mb-16 scroll-mt-32" initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true,
              margin: '-100px'
            }} data-id="element-1112">
                <h2 className="text-2xl font-display mb-6 text-white" data-id="element-1113">
                  3. How We Use Your Information
                </h2>
                <div className="text-gray-300 space-y-4 leading-relaxed" data-id="element-1114">
                  <p data-id="element-1115">We use your information to:</p>
                  <ul className="list-disc pl-5 space-y-2 marker:text-brand-orange" data-id="element-1116">
                    <li data-id="element-1117">Provide and improve our services</li>
                    <li data-id="element-1118">Generate AI-driven insights and reports</li>
                    <li data-id="element-1119">Respond to enquiries and demo requests</li>
                    <li data-id="element-1120">Personalise your experience</li>
                    <li data-id="element-1121">Analyse website performance and user behaviour</li>
                    <li data-id="element-1122">Comply with legal obligations</li>
                  </ul>
                </div>
              </motion.section>

              <div className="h-px w-full bg-white/5 my-12" data-id="element-1123" />

              {/* Section 4 */}
              <motion.section id="sharing" className="mb-16 scroll-mt-32" initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true,
              margin: '-100px'
            }} data-id="element-1124">
                <h2 className="text-2xl font-display mb-6 text-white" data-id="element-1125">
                  4. Sharing of Information
                </h2>
                <div className="text-gray-300 space-y-4 leading-relaxed" data-id="element-1126">
                  <p className="font-medium text-white" data-id="element-1127">
                    We do not sell your personal information.
                  </p>
                  <p data-id="element-1128">We may share information with:</p>
                  <ul className="list-disc pl-5 space-y-2 marker:text-brand-orange" data-id="element-1129">
                    <li data-id="element-1130">
                      Trusted service providers (hosting, analytics, CRM
                      systems)
                    </li>
                    <li data-id="element-1131">Professional advisers (legal, financial)</li>
                    <li data-id="element-1132">Authorities where required by law</li>
                  </ul>
                  <p className="text-sm text-gray-400 mt-4 italic" data-id="element-1133">
                    All third parties are required to handle your data securely.
                  </p>
                </div>
              </motion.section>

              <div className="h-px w-full bg-white/5 my-12" data-id="element-1134" />

              {/* Section 5 */}
              <motion.section id="cookies" className="mb-16 scroll-mt-32" initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true,
              margin: '-100px'
            }} data-id="element-1135">
                <h2 className="text-2xl font-display mb-6 text-white" data-id="element-1136">
                  5. Cookies and Tracking
                </h2>
                <div className="text-gray-300 space-y-4 leading-relaxed" data-id="element-1137">
                  <p data-id="element-1138">We use cookies and similar technologies to:</p>
                  <ul className="list-disc pl-5 space-y-2 marker:text-brand-orange" data-id="element-1139">
                    <li data-id="element-1140">Improve website functionality</li>
                    <li data-id="element-1141">Analyse traffic and usage patterns</li>
                    <li data-id="element-1142">Support marketing and remarketing efforts</li>
                  </ul>
                  <p data-id="element-1143">You can control cookies through your browser settings.</p>
                </div>
              </motion.section>

              <div className="h-px w-full bg-white/5 my-12" data-id="element-1144" />

              {/* Section 6 */}
              <motion.section id="security" className="mb-16 scroll-mt-32" initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true,
              margin: '-100px'
            }} data-id="element-1145">
                <h2 className="text-2xl font-display mb-6 text-white" data-id="element-1146">
                  6. Data Storage and Security
                </h2>
                <div className="text-gray-300 space-y-4 leading-relaxed" data-id="element-1147">
                  <p data-id="element-1148">
                    We take reasonable steps to protect your information using:
                  </p>
                  <ul className="list-disc pl-5 space-y-2 marker:text-brand-orange" data-id="element-1149">
                    <li data-id="element-1150">Secure hosting environments</li>
                    <li data-id="element-1151">Encryption where appropriate</li>
                    <li data-id="element-1152">Access controls and authentication</li>
                  </ul>
                  <p className="text-sm text-gray-400 mt-4 italic" data-id="element-1153">
                    However, no system is completely secure.
                  </p>
                </div>
              </motion.section>

              <div className="h-px w-full bg-white/5 my-12" data-id="element-1154" />

              {/* Section 7 */}
              <motion.section id="international" className="mb-16 scroll-mt-32" initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true,
              margin: '-100px'
            }} data-id="element-1155">
                <h2 className="text-2xl font-display mb-6 text-white" data-id="element-1156">
                  7. International Data Transfers
                </h2>
                <div className="text-gray-300 space-y-4 leading-relaxed" data-id="element-1157">
                  <p data-id="element-1158">
                    As a global service, your data may be stored or processed
                    outside Australia. We take reasonable steps to ensure
                    overseas recipients comply with applicable privacy
                    standards.
                  </p>
                </div>
              </motion.section>

              <div className="h-px w-full bg-white/5 my-12" data-id="element-1159" />

              {/* Section 8 */}
              <motion.section id="rights" className="mb-16 scroll-mt-32" initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true,
              margin: '-100px'
            }} data-id="element-1160">
                <h2 className="text-2xl font-display mb-6 text-white" data-id="element-1161">
                  8. Your Rights
                </h2>
                <div className="text-gray-300 space-y-4 leading-relaxed" data-id="element-1162">
                  <p data-id="element-1163">Depending on your location, you may have rights to:</p>
                  <ul className="list-disc pl-5 space-y-2 marker:text-brand-orange" data-id="element-1164">
                    <li data-id="element-1165">Access your personal data</li>
                    <li data-id="element-1166">Request correction of inaccurate data</li>
                    <li data-id="element-1167">Request deletion of your data</li>
                    <li data-id="element-1168">Withdraw consent for certain uses</li>
                  </ul>
                  <p className="mt-6" data-id="element-1169">
                    To exercise your rights, contact us at:{' '}
                    <a href="mailto:privacy@aijentik.com" className="text-brand-orange hover:text-brand-deep-orange transition-colors" data-id="element-1170">
                      privacy@aijentik.com
                    </a>
                  </p>
                </div>
              </motion.section>

              <div className="h-px w-full bg-white/5 my-12" data-id="element-1171" />

              {/* Section 9 */}
              <motion.section id="third-party" className="mb-16 scroll-mt-32" initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true,
              margin: '-100px'
            }} data-id="element-1172">
                <h2 className="text-2xl font-display mb-6 text-white" data-id="element-1173">
                  9. Third-Party Links
                </h2>
                <div className="text-gray-300 space-y-4 leading-relaxed" data-id="element-1174">
                  <p data-id="element-1175">
                    Our website may contain links to third-party websites. We
                    are not responsible for their privacy practices.
                  </p>
                </div>
              </motion.section>

              <div className="h-px w-full bg-white/5 my-12" data-id="element-1176" />

              {/* Section 10 */}
              <motion.section id="updates" className="mb-16 scroll-mt-32" initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true,
              margin: '-100px'
            }} data-id="element-1177">
                <h2 className="text-2xl font-display mb-6 text-white" data-id="element-1178">
                  10. Updates to This Policy
                </h2>
                <div className="text-gray-300 space-y-4 leading-relaxed" data-id="element-1179">
                  <p data-id="element-1180">
                    We may update this Privacy Policy from time to time. Changes
                    will be posted on this page with an updated revision date.
                  </p>
                </div>
              </motion.section>

              <div className="h-px w-full bg-white/5 my-12" data-id="element-1181" />

              {/* Section 11 */}
              <motion.section id="contact" className="mb-16 scroll-mt-32" initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true,
              margin: '-100px'
            }} data-id="element-1182">
                <h2 className="text-2xl font-display mb-6 text-white" data-id="element-1183">
                  11. Contact Us
                </h2>
                <div className="text-gray-300 space-y-4 leading-relaxed" data-id="element-1184">
                  <p data-id="element-1185">
                    If you have any questions about this Privacy Policy, please
                    contact:
                  </p>
                  <div className="bg-white/5 border border-white/10 rounded-xl p-6 mt-6" data-id="element-1186">
                    <p className="mb-2" data-id="element-1187">
                      <span className="text-white font-medium" data-id="element-1188">Aijentik</span>
                    </p>
                    <p className="mb-2" data-id="element-1189">
                      <span className="text-gray-400" data-id="element-1190">Email:</span>{' '}
                      <a href="mailto:privacy@aijentik.com" className="text-brand-orange hover:text-brand-deep-orange transition-colors" data-id="element-1191">
                        privacy@aijentik.com
                      </a>
                    </p>
                    <p data-id="element-1192">
                      <span className="text-gray-400" data-id="element-1193">Website:</span>{' '}
                      <a href="https://aijentik.com" className="text-brand-orange hover:text-brand-deep-orange transition-colors" data-id="element-1194">
                        aijentik.com
                      </a>
                    </p>
                  </div>
                </div>
              </motion.section>
            </div>
          </div>

          {/* Sticky Sidebar */}
          <div className="hidden lg:block w-72 shrink-0" data-id="element-1195">
            <div className="sticky top-32" data-id="element-1196">
              <h4 className="text-xs font-bold tracking-widest text-gray-500 uppercase mb-6" data-id="element-1197">
                Contents
              </h4>
              <nav className="flex flex-col space-y-1 border-l border-white/10" data-id="element-1198">
                {sections.map(section => <button key={section.id} onClick={() => scrollToSection(section.id)} className={`text-left px-4 py-2 text-sm transition-all duration-200 border-l-[3px] -ml-[2px] ${activeSection === section.id ? 'border-brand-orange text-white font-medium bg-white/5' : 'border-transparent text-gray-500 hover:text-gray-300 hover:border-white/20'}`} data-id="element-1199">
                    {section.title}
                  </button>)}
              </nav>

              <div className="mt-12 p-6 bg-brand-orange/10 border border-brand-orange/20 rounded-xl" data-id="element-1200">
                <p className="text-sm text-brand-orange/90 leading-relaxed" data-id="element-1201">
                  If you have questions about your data, we're here to help.
                </p>
                <a href="mailto:privacy@aijentik.com" className="inline-block mt-4 text-sm font-medium text-white hover:text-brand-orange transition-colors" data-id="element-1202">
                  Contact Privacy Team →
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>;
}